import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Button,
  Collapse,
  Divider,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import api from "../../api/axios";

const actionConfig = {
  PRODUCT_CREATED: { label: "Product Added", color: "success", icon: "➕" },
  PRODUCT_UPDATED: { label: "Product Updated", color: "info", icon: "✏️" },
  PRODUCT_DELETED: { label: "Product Deleted", color: "error", icon: "🗑️" },
  PRODUCT_ARCHIVED: { label: "Product Archived", color: "warning", icon: "📦" },
  PRODUCT_UNARCHIVED: { label: "Product Restored", color: "success", icon: "♻️" },
  PRODUCT_STATUS_CHANGED: { label: "Status Changed", color: "info", icon: "🔄" },
  PROFILE_UPDATED: { label: "Profile Updated", color: "primary", icon: "👤" },
  POLICIES_UPDATED: { label: "Policies Updated", color: "primary", icon: "📋" },
  STORE_SETTINGS_UPDATED: { label: "Store Settings Updated", color: "primary", icon: "⚙️" },
  LOGO_UPDATED: { label: "Logo Updated", color: "info", icon: "🖼️" },
  BANNER_UPDATED: { label: "Banner Updated", color: "info", icon: "🖼️" },
  STOREFRONT_IMAGES_UPDATED: { label: "Storefront Images Updated", color: "info", icon: "🏪" },
  SPONSORSHIP_APPLIED: { label: "Sponsorship Applied", color: "warning", icon: "⭐" },
  SPONSORSHIP_PAYMENT_COMPLETED: { label: "Sponsorship Activated", color: "success", icon: "✅" },
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Truncate long text for preview
const truncateText = (text, maxLength = 100) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

// Format change value for display
const formatChangeValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not set";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const ActivityItem = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const config = actionConfig[activity.action] || {
    label: activity.action,
    color: "default",
    icon: "📌",
  };

  const hasChanges = activity.changes && activity.changes.length > 0;

  return (
    <Paper
      sx={{
        mb: 2,
        p: 2,
        transition: "0.2s",
        "&:hover": {
          bgcolor: "#fafafa",
        },
      }}
    >
      {/* Main row */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor:
              config.color === "success"
                ? "#e8f5e9"
                : config.color === "info"
                ? "#e3f2fd"
                : config.color === "warning"
                ? "#fff3e0"
                : config.color === "error"
                ? "#ffebee"
                : "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography>{config.icon}</Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {activity.description}
            </Typography>
            <Chip
              label={config.label}
              size="small"
              color={config.color}
              sx={{ height: 20, fontSize: 11 }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              📅 {formatDate(activity.createdAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ⏰ {formatTime(activity.createdAt)}
            </Typography>
            {activity.page && (
              <Typography variant="body2" color="text.secondary">
                📍 Page: {activity.page}
              </Typography>
            )}
          </Box>

          {/* Changes preview (collapsed) */}
          {hasChanges && !expanded && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {activity.changes.length} change{activity.changes.length > 1 ? "s" : ""} made
              </Typography>
            </Box>
          )}
        </Box>

        {/* Expand button */}
        {hasChanges && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ flexShrink: 0 }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {/* Expanded changes section */}
      {hasChanges && (
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, ml: 7 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: "text.secondary" }}>
              Changes Made:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {activity.changes.map((change, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                    {change.label}:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {change.oldValue !== undefined && change.oldValue !== null && (
                      <>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "error.main",
                            textDecoration: "line-through",
                            bgcolor: "#ffebee",
                            px: 1,
                            py: 0.25,
                            borderRadius: 0.5,
                          }}
                        >
                          {truncateText(formatChangeValue(change.oldValue), 50)}
                        </Typography>
                        <Typography color="text.secondary">→</Typography>
                      </>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "success.main",
                        bgcolor: "#e8f5e9",
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.5,
                      }}
                    >
                      {truncateText(formatChangeValue(change.newValue), 50)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

const MyActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const [stats, setStats] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchActivities();
  }, [page, actionFilter]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit,
      });
      if (actionFilter) {
        params.append("action", actionFilter);
      }

      const { data } = await api.get(`/vendor/activities?${params}`);
      setActivities(data.activities);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      console.error("Failed to fetch activities:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/vendor/activities/stats");
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          My Activity
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View all your store activities and actions
        </Typography>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: "#f8f9fa" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Total Activities
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {stats.totalActivities || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: "#e8f5e9" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Today's Activities
                </Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {stats.todayActivities || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: "#e3f2fd" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Products Added
                </Typography>
                <Typography variant="h4" fontWeight={700} color="info.main">
                  {stats.byAction?.PRODUCT_CREATED || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: "#fff3e0" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Products Updated
                </Typography>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {stats.byAction?.PRODUCT_UPDATED || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }} size="small">
            <InputLabel>Filter by Action</InputLabel>
            <Select
              value={actionFilter}
              label="Filter by Action"
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {Object.entries(actionConfig).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.icon} {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {actionFilter && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setActionFilter("");
                setPage(1);
              }}
            >
              Clear Filter
            </Button>
          )}
        </Box>
      </Paper>

      {/* Activity List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : activities.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            No activities found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your store activities will appear here
          </Typography>
        </Paper>
      ) : (
        <>
          {activities.map((activity, index) => (
            <ActivityItem key={activity._id || index} activity={activity} />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MyActivity;